<script>
  import { find_next_or_previous_element } from '#lib/util.js';  // Adjust the import path as needed
  import * as public_env from "$app/env/public";
  const LOCALE = public_env.PUBLIC_TIME_LOCALE;

  // Get decimal separator based on locale
  function get_decimal_separator(locale) {
    try {
      // Determine the decimal separator for the locale
      return Intl.NumberFormat(locale)
        .format(1.1)
        .replace(/\d/g, '');
    } catch (e) {
      // Fallback to '.' if there's any error
      return '.';
    }
  }

  let {
    /** @type {string} */
    id,
    ref = $bindable(),
    /** @type {number} */
    value = $bindable(),
    /** @type {string} */
    placeholder,
    /** @type {'.' | ','} */
    decimal_separator = get_decimal_separator(LOCALE),
    /** @type {number} */
    min = -Number.MAX_SAFE_INTEGER,
    /** @type {number} */
    max = Number.MAX_SAFE_INTEGER,
    /** @type {number} */
    step = 0.01,
    /** @type {number}  The step size for whole numbers (before the decimal separator) */
    whole_number_step = 1,
    /** @type {number} Multiplier when shift key is pressed (default 10) */
    shift_multiplier = 10,
    /** @type {number} */
    decimals = 2,
    /** @type {string} */
    variant = 'brand subtle-outlined',
    /** @type {text-heading1|text-heading2|text-heading3|text-body|text-control|text-preamble|text-caption|text-control|text-control} */
    text_style = 'text-control',
    /** @type {string} */
    class: css_class,
    /** @type {function} */
    onkeydown,
    /** @type {(value: number) => void} */
    onchange,
    /** @type {function} */
    onenter,
    /** @type {function} */
    onblur,
    /** @type {function} */
    onfocus,
    /** @type {boolean} */
    disabled,
  } = $props();



  /** @type {string} */
  let display_value = $derived(format_value(value));
  /** @type {number} */
  let caret_position = $state(0);
  /** @type {string | null} Raw input during typing, takes precedence over display_value */
  let raw_input = $state(null);
  /** @type {boolean} Flash warning when value is clamped */
  let show_clamp_warning = $state(false);

  // We use internally a value that is 100 times the actual value (stored as integer instead of float). 
  // This function converts the internal value to the display value.
  function format_value(val) {
    if (typeof val !== 'number' || isNaN(val)) {
      return ''; // Return empty string if val is not a valid number
    }
    return val.toFixed(decimals).replace('.', decimal_separator);
  }

  function clamp_value(val) {
    return Math.min(Math.max(val, min), max);
  }


  function update_caret_position() {
    // Use requestAnimationFrame to sync with browser paint and prevent flicker (setTimeout causes flicker)
    requestAnimationFrame(() => {
      if (ref) {
        ref.setSelectionRange(caret_position, caret_position);
      }
    });
  }

  function handle_input(event) {
    caret_position = event.target.selectionStart;
    
    let new_value = event.target.value.replace(/[^\d.,\-]/g, '').replace(decimal_separator === '.' ? ',' : '.', decimal_separator);
    
    // Remove minus sign if min >= 0
    if (typeof min === 'number' && min >= 0) {
      new_value = new_value.replace(/\-/g, '');
    }
    
    // Handle input starting with decimal separator (e.g., ".2" → "0.2")
    if (new_value.startsWith(decimal_separator)) {
      new_value = '0' + new_value;
      caret_position += 1; // Adjust caret position for the added "0"
    }
    // Handle input starting with negative decimal separator (e.g., "-.2" → "-0.2")
    if (new_value.startsWith('-' + decimal_separator)) {
      new_value = '-0' + new_value.substring(1);
      caret_position += 1; // Adjust caret position for the added "0"
    }
    
    // Handle multiple decimal separators - keep only the first one
    const parts = new_value.split(decimal_separator);
    if (parts.length > 2) {
      new_value = parts[0] + decimal_separator + parts.slice(1).join('');
    }
    
    // Ensure only one minus sign at the start
    const minus_count = (new_value.match(/\-/g) || []).length;
    if (minus_count > 1) {
      new_value = new_value.replace(/\-/g, '');
      if (new_value[0] !== '-') new_value = '-' + new_value;
    }
    
    // Handle completely empty input
    if (new_value === '') {
      value = undefined;
      raw_input = null;
      onchange && onchange(value);
      return;
    }
    
    // Allow partial input during typing (like "-", ".", "-.", "0.", "-0."), will be cleaned up on blur
    const is_partial_input = new_value === '-' || 
                             new_value === decimal_separator || 
                             new_value === '-' + decimal_separator ||
                             new_value === '0' + decimal_separator ||
                             new_value === '-0' + decimal_separator;
    
    if (is_partial_input) {
      // Store raw input and directly set the input value
      raw_input = new_value;
      if (ref) {
        ref.value = new_value;
      }
      update_caret_position();
      return;
    }
    
    // Parse the value for storage, but keep the raw input
    const parsed_value = parseFloat(new_value.replace(decimal_separator, '.'));
    
    if (!isNaN(parsed_value)) {
      value = parsed_value;
      raw_input = new_value;
      // Directly set the input value to preserve raw input during typing
      if (ref) {
        ref.value = new_value;
      }
      onchange && onchange(value);
      update_caret_position();
    }
  }

  function handle_key_down(event) {
    caret_position = event.target.selectionStart;
    
    // Handle enter key press
    if (event.key === 'Enter') {
      if (onenter) {
        onenter(event);
      } else {
        focus_next_focusable(event);
      }
    }

    // Allow all keys when used with Ctrl / Cmd
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Prevent typing minus sign if min >= 0 (negative values not allowed)
    if (event.key === '-' && typeof min === 'number' && min >= 0) {
      event.preventDefault();
      return;
    }
    
    // Prevent typing minus sign if already present or not at the beginning
    // Allow if there's a selection (will be replaced)
    if (event.key === '-') {
      const has_selection = event.target.selectionStart !== event.target.selectionEnd;
      if (!has_selection && (display_value.includes('-') || caret_position > 0)) {
        event.preventDefault();
        return;
      }
    }

    if (!/[\d.,\-]/.test(event.key) && !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Backspace', 'Delete', 'Tab'].includes(event.key)) {
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      handle_arrow_keys(event);
    }

    onkeydown && onkeydown(event);
  }

  function handle_arrow_keys(event) {
    event.preventDefault();
    caret_position = event.target.selectionStart;
    
    const decimal_index = display_value.indexOf(decimal_separator);
    let increment = caret_position <= decimal_index ? whole_number_step : step;
    if (event.shiftKey) increment *= shift_multiplier;

    const direction = event.key === 'ArrowUp' ? 1 : -1;
    
    // Handle undefined/NaN values
    let current_value;
    if (display_value === '' || isNaN(parseFloat(display_value))) {
      // Try to use placeholder if it's a valid number
      const placeholder_value = parseFloat(placeholder?.replace(/[¥€$£₹₽₪₩₱₿]/g,'') || '');
      current_value = !isNaN(placeholder_value) ? placeholder_value : 
                     (min > 0 ? min : max < 0 ? max : 0);
      increment = whole_number_step; // in case of placeholder the caret will always bet at the start of the number
    } else {
      current_value = parseFloat(display_value.replace(decimal_separator, '.'));
    }

    const new_value = clamp_value(current_value + increment * direction);

    // Round the new value to the specified number of decimals
    const multiplier = Math.pow(10, decimals);
    value = Math.round(new_value * multiplier) / multiplier;
    
    // Clear raw_input so the formatted display_value is shown
    raw_input = null;

    if (onchange) onchange(value);
    
    update_caret_position();
  }

  function focus_next_focusable(event) {
    event.preventDefault();
    const next_element = find_next_or_previous_element(
      event.target,
      'next',
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (next_element) {
      next_element.focus();
    }
  }

  function handle_blur(event) {
    // Clear raw input on blur to show formatted value
    raw_input = null;
    
    const target_value = event.target.value.trim();
    
    // Handle invalid/incomplete input (empty, just "-", just decimal separator, "0.", "-0.", etc.)
    const is_invalid = target_value === '' || 
                       target_value === '-' || 
                       target_value === decimal_separator || 
                       target_value === '-' + decimal_separator ||
                       target_value === '0' + decimal_separator ||
                       target_value === '-0' + decimal_separator;
    
    if (is_invalid) {
      // Clear the input field
      value = undefined;
      onchange && onchange(value);
      onblur && onblur(event);
      return;
    }

    const parsed = parseFloat(target_value.replace(decimal_separator, '.'));

    if (isNaN(parsed)) {
      // Clear invalid input
      value = undefined;
      onchange && onchange(value);
    } else {
      // Clamp to min/max range
      const clamped = clamp_value(parsed);
      if (clamped !== parsed) {
        // Value was clamped, show warning flash
        value = clamped;
        show_clamp_warning = true;
        setTimeout(() => {
          show_clamp_warning = false;
        }, 600); // Animation duration
        onchange && onchange(value);
      } else if (clamped !== value) {
        value = clamped;
        onchange && onchange(value);
      }
    }

    onblur && onblur(event);
  }
</script>

<input
  type="text" 
  class='decimal-input selected-outline-on-focus {text_style} {variant} {css_class}'
  class:clamp-warning={show_clamp_warning}
  bind:this={ref}
  {id}
  inputmode="decimal"
  pattern="[0-9.,\-]*"
  autocomplete="off"
  value={raw_input ?? (value === undefined ? '' : display_value)}
  {placeholder}
  oninput={handle_input}
  onkeydown={handle_key_down}
  onblur={handle_blur}
  onfocus={() => {
    // When focusing, if there's a value, set raw_input to allow editing
    if (value !== undefined && ref) {
      raw_input = ref.value;
    }
    onfocus && onfocus();
  }}
  {disabled}
/>

<style lang='postcss'>
  .decimal-input {
    field-sizing: content;
  }
  
  .clamp-warning {
    animation: clamp-flash 0.6s ease-in-out;
  }
  
  @keyframes clamp-flash {
    0%, 100% {
      background-color: transparent;
    }
    50% {
      background-color: var(--color-warning-bg);
    }
  }
</style>